import React from "react";
import GenericModal from "./GenericModal";
import { getOptionText, getOptionValueByText } from "../../utils/optionUtils";

const initialForm = {
  startDepositNumber: "",
  depositCode: "",
  depositName: "",
  depositType: "",
  depositFrequency: "",
  minDepositDuration: "0",
  maxDepositDuration: "0",
  minDepositAmount: "0",
  maxDepositAmount: "0",
  interestFrequency: "",
  interestType: "",
  onLoanExtraInterestRate: "0",
  onLoanMaxInterestDep: "0",
  widowExtraInterestRate: "0",
  seniorCitizenExtraInterestRate: "0",
  womenExtraInterestRate: "0",
  remarks: "",
  active: true,
  editable: true,
};

const AddTermDepositModal = ({
  optionData = [],
  optionLoading,
  optionError,
  open,
  onClose,
  onSave,
  data,
}) => {
  const validateForm = (form) => {
    const newErrors = {};
    const requiredFields = [
      "depositCode",
      "depositName",
      "minDepositDuration",
      "maxDepositDuration",
      "minDepositAmount",
      "maxDepositAmount",
      "interestFrequency",
      "interestType",
      "onLoanExtraInterestRate",
      "onLoanMaxInterestDep",
      "widowExtraInterestRate",
      "seniorCitizenExtraInterestRate",
      "womenExtraInterestRate",
      "depositType",
      "depositFrequency",
    ];
    const fieldLabels = {
      startDepositNumber: "Start Deposit Number",
      depositCode: "Deposit Code",
      depositName: "Deposit Name",
      depositType: "Deposit Type",
      depositFrequency: "Deposit Frequency",
      minDepositDuration: "Minimum Deposit Duration",
      maxDepositDuration: "Maximum Deposit Duration",
      minDepositAmount: "Minimum Deposit Amount",
      maxDepositAmount: "Maximum Deposit Amount",
      interestFrequency: "Interest Frequency",
      interestType: "Interest Type",
      onLoanExtraInterestRate: "Loan Extra Interest Rate",
      onLoanMaxInterestDep: "Max Loan Against Deposit %",
      widowExtraInterestRate: "Additional Int. for Widows",
      seniorCitizenExtraInterestRate: "Additional Int. for Senior Citizens",
      womenExtraInterestRate: "Additional Int. for Women",
      remarks: "Remarks",
      active: "Active",
      editable: "Editable",
    };
    const numericFields = [
      "minDepositDuration",
      "maxDepositDuration",
      "minDepositAmount",
      "maxDepositAmount",
      "onLoanExtraInterestRate",
      "onLoanMaxInterestDep",
    ];

    requiredFields.forEach((field) => {
      if (!form[field]?.toString().trim()) {
        newErrors[field] = `${fieldLabels[field] || field} is required`;
      }
    });

    numericFields.forEach((field) => {
      if (form[field] === "" || isNaN(Number(form[field])) || Number(form[field]) < 0 || Number(form[field]) === 0) {
        newErrors[field] = `${fieldLabels[field] || field} must be greater than 0 and a valid number`;
      }
    });

    if (Number(form.minDepositAmount) >= Number(form.maxDepositAmount)) {
      newErrors.maxDepositAmount =
        "Maximum amount must be greater than minimum";
    }
    if (Number(form.minDepositDuration) >= Number(form.maxDepositDuration)) {
      newErrors.maxDepositDuration =
        "Maximum duration must be greater than minimum";
    }

    return newErrors;
  };

  const mapFormValues = (form, optionData) => {
    // Dynamic mapping via loop
    const mappings = [
      { field: "depositType", optionName: "Deposit Type" },
      { field: "depositFrequency", optionName: "Deposit Frequency" },
      { field: "interestType", optionName: "Interest Type" },
      { field: "interestFrequency", optionName: "Interest Frequency" },
    ];

    const mappedForm = { ...form };
    mappings.forEach(({ field, optionName }) => {
      if (mappedForm[field]) {
        mappedForm[field] = getOptionText(optionData, optionName, mappedForm[field]);
      }
    });
    return mappedForm;
  };

  // New function to map option values when editing
  const mapOptionValues = (data, optionData) => {
    // Map option values to form fields
    // Get interest type and frequency values from options
    const interestTypeValue = getOptionValueByText(
      optionData,
      "Interest Type",
      data.interestType
    );
    const interestFrequencyValue = getOptionValueByText(
      optionData,
      "Interest Frequency",
      data.interestFrequency
    );
    // Get deposit type and frequency values from options
    const depositTypeValue = getOptionValueByText(
      optionData,
      "Deposit Type",
      data.depositType
    );
    const depositFrequencyValue = getOptionValueByText(
      optionData,
      "Deposit Frequency",
      data.depositFrequency
    );
    
    return {
      id: data.id || null,
      startDepositNumber: data.startDepositNumber || "",
      depositCode: data.depositCode || "",
      depositName: data.depositName || "",
      depositType: depositTypeValue || "",
      depositFrequency: depositFrequencyValue || "",
      minDepositDuration: data.minDepositDuration || "",
      maxDepositDuration: data.maxDepositDuration || "",
      minDepositAmount: data.minDepositAmount || "",
      maxDepositAmount: data.maxDepositAmount || "",
      interestFrequency: interestFrequencyValue || "",
      interestType: interestTypeValue || "",
      onLoanExtraInterestRate: data.onLoanExtraInterestRate || "",
      onLoanMaxInterestDep: data.onLoanMaxInterestDep || "",
      widowExtraInterestRate: data.widowExtraInterestRate || "",
      seniorCitizenExtraInterestRate: data.seniorCitizenExtraInterestRate || "",
      womenExtraInterestRate: data.womenExtraInterestRate || "",
      remarks: data.remarks || "",
      active: data.active ?? true,
      editable: data.editable ?? true,
    };
  };

  const fields = [
    {
      name: "startDepositNumber",
      label: "Start Deposit No.",
      helper: "Auto or manual entry",
      size: { xs: 12, sm: 12, md:3 }
    },
    {
      name: "depositCode",
      label: "Deposit Code",
      helper: "Unique code",
      size: { xs: 12, sm: 12, md:3 }
    },
    {
      name: "depositName",
      label: "Deposit Name",
      helper: "Descriptive name",
      size: { xs: 12, sm: 12, md:6 }
    },
    {
      name: "depositType",
      label: "Deposit Type",
      helper: "e.g. Fixed/Recurring",
      type: "select",
      optionName: "Deposit Type",
      size: { xs: 12, sm: 12, md:3 }
    },
    {
      name: "depositFrequency",
      label: "Deposit Frequency",
      helper: "e.g. Monthly",
      type: "select",
      optionName: "Deposit Frequency",
      size: { xs: 12, sm: 12, md:3 }
    },
     {
      name: "minDepositAmount",
      label: "Min Amount",
      type: "number",
      adornment: "₹",
      size: { xs: 12, sm: 12, md:3 }
    },
    {
      name: "maxDepositAmount",
      label: "Max Amount",
      type: "number",
      adornment: "₹",
      size: { xs: 12, sm: 12, md:3 }
    },
    {
      name: "minDepositDuration",
      label: "Min Duration (months)",
      type: "number",
      size: { xs: 12, sm: 12, md:3 }
    },
    {
      name: "maxDepositDuration",
      label: "Max Duration (months)",
      type: "number",
      size: { xs: 12, sm: 12, md:3 }
    },
     {
      name: "interestType",
      label: "Interest Type",
      helper: "Simple/Compound",
      type: "select",
      optionName: "Interest Type",
      size: { xs: 12, sm: 12, md:3 }
    },
    {
      name: "interestFrequency",
      label: "Interest Frequency",
      helper: "e.g. Quarterly",
      type: "select",
      optionName: "Interest Frequency",
      size: { xs: 12, sm: 12, md:3 }
    },
    {
      name: "onLoanExtraInterestRate",
      label: "Extra Interest Rate (%) on Loan",
      type: "number",
      helper: "Additional interest rate for loans against deposit",
      adornment: "%",
      size: { xs: 12, sm: 12, md:3 }
    },
    {
      name: "onLoanMaxInterestDep",
      label: "Max Loan Against Dep (%)",
      type: "number",
      helper: "Maxium loan as % of deposit",
      adornment: "%",
      size: { xs: 12, sm: 12, md:3 }
    },
    {
      name: "widowExtraInterestRate",
      label: "Additional Int. (%) for Widows",
      helper: "Additional interest rate for Widows",
      type: "number",
      size: { xs: 12, sm: 12, md:3 }
    },
    {
      name: "seniorCitizenExtraInterestRate",
      label: "Additional Int. (%) for Senior Citizens",
      helper: "Additional interest rate for Senior Citizens",
      type: "number",
      size: { xs: 12, sm: 12, md:3 }
    },
    {
      name: "womenExtraInterestRate",
      label: "Additional Int. (%) for Women",
      helper: "Additional interest rate for Women",
      type: "number",
      size: { xs: 12, sm: 12, md:3 }
    },
    { 
      name: "remarks", 
      label: "Remarks", 
      multiline: true, 
      rows: 2 ,
      size: { xs: 12, sm: 12, md:12 }
    },
  ];

  return (
    <GenericModal
      open={open}
      onClose={onClose}
      onSave={onSave}
      data={data ? mapOptionValues(data, optionData) : null} 
      title="Term Deposit"
      fields={fields}
      initialForm={initialForm}
      validateForm={validateForm}
      mapFormValues={mapFormValues}
      optionData={optionData}
      saveButtonText="Save Term Deposit"
      updateButtonText="Update Term Deposit"
    />
  );
};

export default AddTermDepositModal;
